/**
 * Declaración de las columnas de la grilla de Solicitudes de Stock, para <GridColumnSelector /> y
 * `useColumnVisibility` (src/commons/grid/).
 *
 * Vive en su propio módulo por lo mismo que `inventarioSolicitudesFilterFields.js`: lo consumen el
 * selector (para listar las opciones) y la grilla (para decidir qué celda se ve).
 *
 * TRES REGLAS al tocar esta lista:
 *
 * 1. El ORDEN es el del `thead`. Es el orden en que el usuario ve las opciones en el selector, y que
 *    no coincida con la grilla se nota enseguida.
 *
 * 2. El `messageId` es el MISMO del encabezado de la columna. Si el selector dijera algo distinto de
 *    lo que dice el `th`, el usuario no podría relacionarlos.
 *
 * 3. El `name` de las columnas ordenables coincide con su clave de orden
 *    (`ItemStockSolicitudRepository::CAMPOS_ORDENABLES`), a propósito: así el `show` del encabezado
 *    y su `field` de ordenamiento se escriben con el mismo nombre y no hay dos vocabularios para la
 *    misma columna. `deposito`, `pendiente` y `solicitante` no son ordenables y por eso solo existen
 *    acá.
 *
 * NINGUNA COLUMNA ESTÁ GATEADA POR CONFIG, a diferencia de Móviles o Tickets: esta pantalla no tiene
 * flags por columna, así que no hace falta el `enabled` ni el sufijo `Enabled` en los nombres. Si
 * alguna vez se gatea una, se agrega `enabled` acá y el hook la saca del selector sola.
 *
 * `fixed: true` = no se puede ocultar y no aparece en el selector. Solo la de acciones: en el
 * selector sería una casilla sin etiqueta, y ocultarla dejaría la fila sin sus botones y sin una
 * forma evidente de recuperarlos.
 */

export const INVENTARIO_SOLICITUDES_COLUMNAS_STORAGE_KEY = 'InventarioSolicitudesGrid_columnas';

/**
 * Las columnas de UNA vista.
 *
 * `vistas` declara en qué pestañas existe una columna; sin la clave, en todas. Se resuelve al eje
 * `enabled` del hook de visibilidad, que es justo el que significa "esta columna no existe acá": la
 * saca de la tabla Y del selector de columnas, en vez de dejarla ofrecida para prender algo que no
 * aplica.
 *
 * Es `enabled` y no la lista de ocultas a propósito: lo que el usuario eligió ocultar se persiste
 * por nombre y sobrevive al cambio de pestaña, sin que esto lo pise.
 */
export const columnasDeVista = (vista) =>
	INVENTARIO_SOLICITUDES_COLUMNAS.map((c) =>
		!c.vistas || c.vistas.indexOf(vista) !== -1 ? c : Object.assign({}, c, { enabled: false })
	);

export const INVENTARIO_SOLICITUDES_COLUMNAS = [
	{
		// La casilla de selección masiva. `fixed` como la de acciones: en el selector aparecería como
		// una casilla sin etiqueta, y ocultarla dejaría filas seleccionadas sin forma de deseleccionar.
		name: 'seleccion',
		messageId: 'inventarioSolicitudes.grid.column_seleccion',
		label: 'Selección',
		fixed: true,
	},
	{
		// El id de la FILA (el detalle), que es el único identificador que señala una línea concreta:
		// una solicitud de cuatro repuestos son cuatro filas, y el id de la cabecera es el mismo en
		// las cuatro. Es lo que se usa para nombrar una línea puntual —en un reporte, en la URL de un
		// endpoint, o hablando con otro—.
		name: 'id',
		messageId: 'inventarioSolicitudes.grid.column_id',
		label: 'ID',
	},
	{
		name: 'fecha',
		messageId: 'inventarioSolicitudes.grid.column_fecha',
		label: 'Fecha',
	},
	{
		name: 'ticket',
		messageId: 'inventarioSolicitudes.grid.column_ticket',
		label: 'Ticket',
	},
	{
		name: 'numeroDeParte',
		messageId: 'inventarioSolicitudes.grid.column_numero_de_parte',
		label: 'Nº de parte',
	},
	{
		name: 'descripcion',
		messageId: 'inventarioSolicitudes.grid.column_item',
		label: 'Repuesto',
	},
	{
		name: 'deposito',
		messageId: 'inventarioSolicitudes.grid.column_deposito',
		label: 'Depósito',
	},
	{
		// Va pegada a Depósito, no al lado de las cantidades del pedido: es un dato DEL DEPÓSITO —qué
		// hay hoy en ese almacén—, no del pedido. Leídas juntas contestan "¿este depósito tiene con
		// qué?" antes de mirar cuánto se pidió.
		//
		// Solo en Pendientes, por lo mismo que `pendiente`: esa pregunta es "¿puedo despachar esto?",
		// y sobre una línea cerrada no hay nada que despachar. Además el número es el stock de HOY, así
		// que en una finalizada hace meses no dice nada sobre cómo se resolvió.
		name: 'disponible',
		messageId: 'inventarioSolicitudes.grid.column_disponible',
		label: 'Disponible',
		vistas: ['pendientes'],
	},
	{
		name: 'cantidad',
		messageId: 'inventarioSolicitudes.grid.column_cantidad',
		label: 'Solicitado',
	},
	{
		name: 'despachado',
		messageId: 'inventarioSolicitudes.grid.column_despachado',
		label: 'Despachado',
	},
	{
		// Solo en Pendientes: en Finalizadas vale 0 en todas las filas —una línea cerrada no tiene
		// nada por entregar, ni siquiera la diferencia de una rechazada parcialmente— y una columna
		// de ceros ocupa lugar sin decir nada.
		name: 'pendiente',
		messageId: 'inventarioSolicitudes.grid.column_pendiente',
		label: 'Pendiente',
		vistas: ['pendientes'],
	},
	{
		// La contracara, en el MISMO lugar: en Finalizadas la pregunta no es cuánto falta sino cuándo
		// terminó. Declaradas una al lado de la otra a propósito, así la columna no se mueve de sitio
		// al cambiar de pestaña.
		name: 'fechaCierre',
		messageId: 'inventarioSolicitudes.grid.column_fecha_cierre',
		label: 'Fecha de cierre',
		vistas: ['finalizadas'],
	},
	{
		name: 'solicitante',
		messageId: 'inventarioSolicitudes.grid.column_solicitante',
		label: 'Solicitante',
	},
	{
		name: 'acciones',
		messageId: 'inventarioSolicitudes.grid.column_acciones',
		label: 'Acciones',
		fixed: true,
	},
];
